import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase-client";

interface UseSignedUrlOptions {
  bucket: string;
  path: string | null;
  expiresIn?: number;
  transform?: {
    width?: number;
    height?: number;
    quality?: number;
    format?: string;
    resize?: string;
  };
  enabled?: boolean;
}

export function useSignedUrl(options: UseSignedUrlOptions) {
  const { bucket, path, expiresIn = 300, transform, enabled = true } = options;
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled || !path) {
      setSignedUrl(null);
      return;
    }

    let cancelled = false;

    const fetchSignedUrl = async () => {
      try {
        setLoading(true);
        setError(null);

        // Call storage API directly - uses user's auth token and RLS policies
        const { data, error: storageError } = await supabase.storage
          .from(bucket)
          // transform.format/resize are loose strings here; Supabase types them as enums
          .createSignedUrl(
            path,
            expiresIn,
            transform ? { transform: transform as Record<string, unknown> } : undefined,
          );

        if (storageError) throw storageError;
        if (!cancelled && data?.signedUrl) {
          setSignedUrl(data.signedUrl);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to get signed URL");
          console.error("Signed URL error:", err);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchSignedUrl();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bucket, path, expiresIn, enabled, JSON.stringify(transform)]);

  return { signedUrl, loading, error };
}
